import { AbortControllerManager } from '../abortControllerManager';
import { authClient } from '../client';
import {
  IGetUsersQueryParams,
  IUserInviteRequest,
} from '@/pages/AdminPanel/types';
import type {
  IChangeUserBillingInstitutionRequest,
  IUpdateUserByRoleAndInstitutionRequest,
  IUserData,
  IUserInviteServer,
  TInviteType,
} from '@/types';
import { ITemplateResponse } from '@/types/common';
import { convertToQueryParams } from '@/utils/convertToQueryParams';

class UsersApi extends AbortControllerManager {
  private USERS_URL = '/v2/users';
  private USER_INVITE_URL = '/v2/user-invite';

  constructor() {
    super();
  }

  // INVITE USER
  sendInvite(payload: IUserInviteRequest) {
    const signal = this.createAbortController('sendInvite');
    return authClient.post(this.USER_INVITE_URL, payload, { signal });
  }

  /**
   * Fetches the pending or expired user invites for an institution.
   * @param institutionId - The institution to fetch invites for.
   * @param state - Which invite state to fetch, `'pending'` or `'expired'`.
   * @returns A template response containing the matching invites.
   */
  getInvites(
    institutionId: string,
    state: TInviteType,
  ): Promise<ITemplateResponse<IUserInviteServer[]>> {
    const signal = this.createAbortController(
      `getInvites-${institutionId}-${state}`,
    );
    return authClient.get(
      `${this.USER_INVITE_URL}?institution_id=${institutionId}&state=${state}`,
      {
        signal,
      },
    );
  }

  /**
   * Cancels a pending user invite.
   * @param inviteId - The id of the invite to cancel.
   * @returns The API response for the cancellation request.
   */
  cancelInvite(inviteId: string): Promise<void> {
    const signal = this.createAbortController(`cancelInvite-${inviteId}`);
    return authClient.post(
      `${this.USER_INVITE_URL}/${inviteId}/cancel`,
      undefined,
      { signal },
    );
  }

  // USERS API
  /**
   * Fetches the list of users, optionally filtered by role and/or institution.
   * @param paramsObj - Optional query params to filter the returned users.
   * @returns A template response containing the matching users.
   */
  getUsers(
    paramsObj?: IGetUsersQueryParams,
  ): Promise<ITemplateResponse<IUserData[]>> {
    const signal = this.createAbortController(
      `getUsers-${paramsObj?.role ?? ''}-${paramsObj?.institution}`.trim(),
    );
    const params = paramsObj
      ? convertToQueryParams({ ...paramsObj })
      : undefined;
    return authClient.get(
      params ? `${this.USERS_URL}?${params}` : this.USERS_URL,
      {
        signal,
      },
    );
  }

  /**
   * Fetches a single user by their id.
   * @param id - The user's id.
   * @returns A template response containing the matching user.
   */
  getUserById(id: string): Promise<ITemplateResponse<IUserData>> {
    const signal = this.createAbortController(`getUserById-${id}`);
    return authClient.get(`${this.USERS_URL}/${id}`, {
      signal,
    });
  }

  /**
   * Updates a user's role and/or institution assignment.
   * @param body - The user id and the role/institution to assign.
   * @returns The API response for the update request.
   */
  updateUserByInstitutionRoles(body: IUpdateUserByRoleAndInstitutionRequest) {
    const url = `${this.USERS_URL}/institution-roles`;
    const signal = this.createAbortController(
      `updateRoles-${body.user_id}-${body.role}-${body.institution_id}`,
    );
    return authClient.post(url, body, { signal });
  }
  /**
   * Changes which institution a user's billing is currently attributed to.
   * @param body - The user id and the institution id to bill going forward.
   * @returns The API response for the billing change request.
   */
  changeUserBillingInstitution(body: IChangeUserBillingInstitutionRequest) {
    const url = `${this.USERS_URL}/institution-roles/billing`;
    const signal = this.createAbortController(
      `changeUserBilling-${body.user_id}-${body.institution_id}`,
    );
    return authClient.patch(url, body, { signal });
  }

  setActiveStatus(userId: string, active: boolean) {
    const url = `${this.USERS_URL}/${userId}`;
    const signal = this.createAbortController(`setActiveStatus-${userId}`);
    return authClient.patch(url, { is_active: active }, { signal });
  }

  /**
   * Activates a user pending activation, optionally setting their email.
   * @param userId - The id of the user to activate.
   * @param email - The email to set for the user; when omitted, the request body is empty.
   * @returns A template response containing the activated user.
   */
  activateUser(
    userId: string,
    email?: string,
  ): Promise<ITemplateResponse<IUserData>> {
    const url = `${this.USERS_URL}/${userId}/activate`;
    const signal = this.createAbortController(`activateUser-${userId}`);
    return authClient.post(url, email ? { email } : {}, { signal });
  }

  /**
   * Removes a user's institution role.
   * @param roleId - The id of the user institution role to remove.
   * @returns The API response for the deletion.
   */
  removeUserRole(roleId: string) {
    const url = `${this.USERS_URL}/roles/${roleId}`;
    const signal = this.createAbortController(`removeUserRole-${roleId}`);
    return authClient.delete(url, { signal });
  }
}

export const usersApi = new UsersApi();
